import React, { useRef } from 'react'
import { components, useTranslation } from 'cng-web-lib'
import Namespace from '../../../constants/locale/Namespace'
import CollapsibleSection from '../../../components/ui/CollapsibleSection'
import FormProperties from './PartyIdentifierSectionFormProperties'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Divider, Grid, Tooltip } from '@material-ui/core'
import PartyIdentifierFieldsPlaceholder from './PartyIdentifierFieldsPlaceholder'
import _ from 'lodash'
import useCustomYupValidationResolver from 'src/components/aciacehighway/hooks/useCustomYupValidationResolver'

const {
  button: { CngButton, CngIconButton }
} = components

const { initialValues, makeValidationSchema } = FormProperties.formikProps

function PartyIdentifierSection(props) {
  const { title } = props

  const { getValues, setValue, watch } = useFormContext()
  const partyIdentifiers = watch('partyIdentifierSection')
  const generatedIndex = useRef(0)

  function handleAddPartyIdentifier(data) {
    const partyIdentifiers = [...getValues('partyIdentifierSection'), data]

    setValue('partyIdentifierSection', partyIdentifiers)
  }

  function handleDeletePartyIdentifier(index) {
    const partyIdentifiers = [...getValues('partyIdentifierSection')]

    partyIdentifiers.splice(index, 1)

    setValue('partyIdentifierSection', partyIdentifiers)
  }

  function handleEditPartyIdentifier(index, data) {
    const partyIdentifiers = [...getValues('partyIdentifierSection')]

    partyIdentifiers[index] = data

    setValue('partyIdentifierSection', partyIdentifiers)
  }

  return (
    <CollapsibleSection defaultExpanded={!_.isEmpty(partyIdentifiers)} title={title}>
      <Grid container spacing={2}>
        {_.isEmpty(partyIdentifiers) ? (
          <Grid item xs={12}>
            <PartyIdentifierFieldsPlaceholder onAdd={handleAddPartyIdentifier} />
          </Grid>
        ) : (
          partyIdentifiers.map((partyIdentifier, index) => (
            <React.Fragment key={++generatedIndex.current}>
              <Grid item xs={12}>
                <PartyIdentifierEntry
                  partyIdentifiers={partyIdentifier}
                  onAdd={handleAddPartyIdentifier}
                  onDelete={() => handleDeletePartyIdentifier(index)}
                  onEdit={(data) => handleEditPartyIdentifier(index, data)}
                />
              </Grid>
              {index + 1 !== partyIdentifiers.length && (
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              )}
            </React.Fragment>
          ))
        )}
      </Grid>
    </CollapsibleSection>
  )
}

function PartyIdentifierEntry(props) {
  const { partyIdentifiers, onAdd, onDelete, onEdit } = props

  const { translate } = useTranslation(Namespace.NBCI_USCA_PARTY_MASTER)
  const methods = useForm({
    defaultValues: partyIdentifiers,
    resolver: useCustomYupValidationResolver(makeValidationSchema(translate))
  })
  const {
    formState: { isDirty }
  } = methods

  function handleEditPartyIdentifier(data) {
    methods.reset(data)
    onEdit(data)
  }

  return (
    <FormProvider {...methods}>
      <Grid alignItems='center' container spacing={1}>
        <Grid item xs={12} sm>
          <FormProperties.Fields />
        </Grid>
        <Grid item xs={12} sm='auto'>
          <Grid justify='flex-end' container spacing={1}>
            {isDirty ? (
              <>
                <Grid item xs='auto'>
                  <CngButton
                    color='secondary'
                    onClick={() => methods.reset()}
                    size='medium'
                  >
                    Reset
                  </CngButton>
                </Grid>
                <Grid item xs='auto'>
                  <CngButton
                    color='primary'
                    onClick={methods.handleSubmit(handleEditPartyIdentifier)}
                    size='medium'
                  >
                    Save changes
                  </CngButton>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs='auto'>
                  <Tooltip placement='bottom' title='Add'>
                    <span>
                      <CngIconButton
                        icon={['fal', 'plus']}
                        onClick={() => onAdd(initialValues)}
                        size='small'
                        type='outlined'
                      />
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs='auto'>
                  <Tooltip placement='bottom' title='Clone'>
                    <span>
                      <CngIconButton
                        icon={['fal', 'copy']}
                        onClick={() => onAdd({ ...partyIdentifiers, id: undefined })}
                        size='small'
                        type='outlined'
                      />
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs='auto'>
                  <Tooltip placement='bottom' title='Delete'>
                    <span>
                      <CngIconButton
                        icon={['fal', 'trash']}
                        onClick={() => onDelete(partyIdentifiers)}
                        size='small'
                        type='outlined'
                      />
                    </span>
                  </Tooltip>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </FormProvider>
  )
}

export default PartyIdentifierSection
